import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Callout,
	FlexGap,
	GraphQLErrorMessage,
	Label,
	Select,
	SettingsSection,
	tokens,
	useToasts,
} from '@treams/treams-ui';

export function GrowthPathScaleSettings() {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { localize } = useLocalize();

	const scaleQuery = graphql.useGrowthPathScaleQuery();
	const levelSetsQuery = graphql.useCompetencyLevelSetsQuery({
		variables: { isArchived: false, first: 1000 },
	});
	const [setScale, { loading, error }] = graphql.useSetGrowthPathScaleMutation({
		refetchQueries: [{ query: graphql.GrowthPathScaleDocument }],
		awaitRefetchQueries: true,
	});

	const scale = scaleQuery.data?.viewer?.workspace?.growthPathScale;

	const levelSetOptions = (levelSetsQuery.data?.competencyLevelSets.edges ?? []).map(
		({ node }) => ({
			value: node.id,
			title: localize(node.title).text,
		}),
	);
	const targetOptions = (scale?.levels ?? []).map((level, index) => ({
		value: String(index),
		title: localize(level.title).text,
	}));

	const save = async (input: graphql.SetGrowthPathScaleInput) => {
		try {
			await setScale({ variables: { input } });
			addToast(t('settings.growthPath.scale.saved'), { appearance: 'success', autoDismiss: true });
		} catch {
			// error state is shown via GraphQLErrorMessage
		}
	};

	return (
		<SettingsSection
			title={t('settings.growthPath.scale.title')}
			description={t('settings.growthPath.scale.description')}
		>
			<FlexGap flexDirection="column" gap={tokens.gap.element}>
				<Box width={300}>
					<Label mb={1}>{t('settings.growthPath.scale.levelSet')}</Label>
					<Select
						clearable
						options={levelSetOptions}
						selectedValue={scale?.competencyLevelSetID}
						placeholder={t('settings.growthPath.scale.levelSet.none')}
						disabled={loading}
						onChange={(value) => save({ competencyLevelSetID: value ?? null })}
					/>
				</Box>
				{scale && (
					<Box width={300}>
						<Label mb={1}>{t('settings.growthPath.scale.target')}</Label>
						<Select
							options={targetOptions}
							selectedValue={String(scale.targetLevelIndex)}
							disabled={loading}
							onChange={(value) =>
								value !== undefined &&
								save({
									competencyLevelSetID: scale.competencyLevelSetID,
									targetLevelIndex: Number(value),
								})
							}
						/>
					</Box>
				)}
				<Callout color="primary">{t('settings.growthPath.scale.changeWarning')}</Callout>
				{error?.graphQLErrors[0] && <GraphQLErrorMessage error={error.graphQLErrors[0]} />}
			</FlexGap>
		</SettingsSection>
	);
}
